/**
 * Per-language re-ordering of code tokens.
 *
 * The codebook's `tokens` array is in a single fixed order (originally
 * authored by the Italian-speaking game designer). When the game is played
 * in another language whose natural word order differs (e.g. English puts
 * adjectives before nouns, Italian after), the order numbers on the frames
 * should reflect the play language so the sentence reads naturally.
 *
 * Strategy: use each token's per-language gloss + the official translation
 * as the source of truth. For each token, find where its gloss first appears
 * in the translation; sort tokens by position. Compound pairs (a token with
 * `compoundWithNext` + its successor) are kept adjacent and share one order
 * number, matching how the physical game handles "Art + Person = Scientist".
 *
 * Heuristic — if a gloss can't be located in the translation (small words
 * like "I", "to", grammatical particles), that token keeps its original
 * position in the sequence as a tiebreaker.
 */

import type { CodeSentence, SentenceToken } from "../game/types";
import { iconKey } from "../game/types";
import type { Lang } from "../i18n";

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findPosition(gloss: string, sentence: string): number {
  if (!gloss || !sentence) return -1;
  const sNorm = normalize(sentence);
  // Try each comma/slash/paren-separated alternative gloss.
  const alts = gloss.split(/[\/\(\),]/).map((s) => normalize(s)).filter(Boolean);
  let minPos = Infinity;
  for (const alt of alts) {
    if (alt.length >= 3) {
      const wholePos = sNorm.indexOf(alt);
      if (wholePos >= 0 && wholePos < minPos) minPos = wholePos;
    }
    const words = alt.split(" ").filter(Boolean);
    const isSingleWordAlt = words.length === 1;
    for (const w of words) {
      let pos = -1;
      if (w.length >= 3) {
        pos = sNorm.indexOf(w);
        // Stem retry (helps think→thinks, build→builds, etc. — verb conjugations).
        if (pos < 0 && w.length >= 4) {
          const stem = w.slice(0, 4);
          const stemPos = sNorm.indexOf(stem);
          if (stemPos >= 0) pos = stemPos;
        }
      } else if (isSingleWordAlt) {
        // Short word that IS the whole gloss (e.g. "I"). Use word-boundary regex.
        // We deliberately do NOT match short words inside multi-word glosses,
        // so "a" inside "a lot" doesn't grab the article at position 0.
        const re = new RegExp(`(^|\\s)${w}(\\s|$)`);
        const match = re.exec(sNorm);
        if (match) pos = match.index + (match[1] ? 1 : 0);
      }
      if (pos >= 0 && pos < minPos) minPos = pos;
    }
  }
  return minPos === Infinity ? -1 : minPos;
}

/**
 * Group tokens — each group is either a singleton or a compound pair that
 * stays adjacent. Returns arrays of indices into code.tokens.
 */
function groupTokens(tokens: SentenceToken[]): number[][] {
  const groups: number[][] = [];
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].compoundWithNext && i + 1 < tokens.length) {
      groups.push([i, i + 1]);
      i++;
    } else {
      groups.push([i]);
    }
  }
  return groups;
}

/**
 * Returns an array of indices into `code.tokens` describing the natural reading
 * order for the given language.
 */
export function computeLanguageOrder(code: CodeSentence, lang: Lang): number[] {
  const sentence = code.translations[lang] ?? code.translations.en ?? "";
  if (!sentence) return code.tokens.map((_, i) => i);

  const groups = groupTokens(code.tokens);

  // Pass 1: find position per group.
  type Pos = { groupIdx: number; position: number; found: boolean };
  const positions: Pos[] = groups.map((g, groupIdx) => {
    const firstTokenIdx = g[0];
    const tok = code.tokens[firstTokenIdx];
    const glossEntry = code.glossary.find((ge) => ge.iconKey === iconKey(tok.icon));
    let glossText = glossEntry?.gloss[lang] ?? glossEntry?.gloss.en ?? "";
    if (glossEntry?.compound) {
      glossText = glossEntry.compound[lang] ?? glossEntry.compound.en ?? glossText;
    }
    const pos = findPosition(glossText, sentence);
    return { groupIdx, position: pos, found: pos >= 0 };
  });

  // Pass 2: interpolate positions for unfound groups so they slot in between
  // their original neighbours rather than getting dumped at the end.
  for (let i = 0; i < positions.length; i++) {
    if (positions[i].found) continue;
    let prev = -1, next = -1;
    for (let j = i - 1; j >= 0; j--) if (positions[j].found) { prev = j; break; }
    for (let j = i + 1; j < positions.length; j++) if (positions[j].found) { next = j; break; }
    if (prev >= 0 && next >= 0) {
      const span = positions[next].position - positions[prev].position;
      const fraction = (i - prev) / (next - prev);
      positions[i].position = positions[prev].position + span * fraction;
    } else if (prev >= 0) {
      positions[i].position = positions[prev].position + (i - prev) * 0.5;
    } else if (next >= 0) {
      positions[i].position = positions[next].position - (next - i) * 0.5;
    } else {
      positions[i].position = i;
    }
  }

  positions.sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.groupIdx - b.groupIdx;
  });

  const order: number[] = [];
  for (const p of positions) {
    for (const idx of groups[p.groupIdx]) order.push(idx);
  }
  return order;
}

/**
 * Returns the tokens reordered for the given language, with `order` numbers
 * reassigned. Compound pairs share an order number (matching the physical
 * game's "1+1, 2, 3, …" notation).
 *
 * The token's `icon` (and therefore its position on the board) is unchanged —
 * only the numeric badge in the frame's bottom-right corner rearranges.
 */
export function getOrderedTokensForLang(code: CodeSentence, lang: Lang): SentenceToken[] {
  const order = computeLanguageOrder(code, lang);
  const result: SentenceToken[] = [];
  let nextOrderNum = 1;
  for (let i = 0; i < order.length; i++) {
    const origIdx = order[i];
    const baseToken = { ...code.tokens[origIdx] };
    if (i > 0 && result[i - 1]?.compoundWithNext) {
      baseToken.order = result[i - 1].order;
    } else {
      baseToken.order = nextOrderNum++;
    }
    result.push(baseToken);
  }
  return result;
}
